import {
  getMuxHostedThumbnailUrl,
  getSelfHostedThumbnailUrl,
} from '@src/utility/story';
import { LogManager } from '@src/utility/Logger';

import type { ReelBeat, ReelChapter, ReelOutline } from './types';
import type { ClipFragment } from '@src/services/AIClipProducer';
import type { ReelClipResults } from '@src/services/reelClipProducer/types';

const logger = LogManager.getLogger('ReelProducer:clipSequenceUtils');

/**
 * Get the beat key that links to ReelClipResults Map.
 * Keys follow the format "chapterOrder.beatOrder" (e.g., "1.1", "2.3").
 */
export function getBeatKey(chapter: ReelChapter, beat: ReelBeat): string {
  return `${chapter.order}.${beat.order}`;
}

/**
 * Iterates chapters/beats in order, skips excluded beats and beats without
 * a resolved clipFragment, and returns the ordered ClipFragment array.
 */
export function extractOrderedClipFragments(
  outline: ReelOutline,
  clipResults: ReelClipResults,
  excludedBeats: Set<string>,
): ClipFragment[] {
  const fragments: ClipFragment[] = [];

  for (const chapter of outline.chapters) {
    for (const beat of chapter.beats) {
      const key = getBeatKey(chapter, beat);
      if (excludedBeats.has(key)) continue;

      const result = clipResults.get(key);
      if (!result) continue;

      const selected = result.options[result.selectedOptionIndex];
      if (!selected) {
        logger.warn('extractOrderedClipFragments: selectedOptionIndex out of bounds', {
          key,
          selectedOptionIndex: result.selectedOptionIndex,
          optionsLength: result.options.length,
        });
        continue;
      }
      if (!selected.clipFragment) continue;

      fragments.push(selected.clipFragment);
    }
  }

  return fragments;
}

/**
 * Resolve the best available MP4 URL from the story's original video.
 * Prefers low quality for preview, falls back through medium/high.
 */
export function resolveVideoSrc(story: { originalVideo?: { video?: { mp4UrlLow?: string; mp4Url?: string; mp4UrlMedium?: string; mp4UrlHigh?: string } } } | null | undefined): string {
  const ov = story?.originalVideo;
  return (
    ov?.video?.mp4UrlLow ||
    ov?.video?.mp4Url ||
    ov?.video?.mp4UrlMedium ||
    ov?.video?.mp4UrlHigh ||
    ''
  );
}

/**
 * Get a thumbnail URL for the story's original video at the given timecode.
 */
export function getStoryThumbnailUrl(
  story: { originalVideo?: StoryVideoLike } | null | undefined,
  timecode?: number,
): string {
  const ov = story?.originalVideo;
  if (!ov) return '';

  const muxId = ov.video?.muxPlaybackId;
  if (muxId) return getMuxHostedThumbnailUrl(muxId, timecode);
  return getSelfHostedThumbnailUrl(ov as Parameters<typeof getSelfHostedThumbnailUrl>[0], timecode);
}

type StoryVideoLike = {
  video?: {
    muxPlaybackId?: string;
    thumbnailUrl?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
};
